import { AttemptId, QuizId, QuizIndex, Serializer } from "./index";

declare var scores: AttemptScore<any, any> | undefined;

export type AttemptScore<A extends AttemptId, Z extends QuizId> = Record<QuizIndex, SectionScore<A, Z>>;

export interface OverviewScore<A extends AttemptId, Z extends QuizId> {
	quiz: Z;
	data: SectionScore<A, Z>;
}

export interface SectionScore<A extends AttemptId, Z extends QuizId> {
	id: A;
	answered_count: string;
	correct_count: string;
	total_count: string;
}

export interface ReviewScore {
	section: string;
	time: string;
	correct: number;
	incorrect: number;
	unanswered: number;
	total: number;
	percent: number;
}

export class ScoreDataSource<A extends AttemptId, Z extends QuizId> {
	quizIdSerializer: Serializer<Z>
	constructor(quiz: Serializer<Z>) {
		this.quizIdSerializer = quiz;
	}

	/**
	 * @returns {Promise<{set:{test:{
	 *     answered_count:string,
	 *     complete:boolean,
	 *     correct_count:string,
	 *     finished:boolean,
	 *     id:string,
	 *     incorrect_count:string,
	 *     name:string,
	 *     percent_correct:string,
	 *     quiz_id:string,
	 *     started:string,
	 *     total_count:string,
	 *     total_time:string
	 * }}}>}
	 */
	getOverviewScores(): Promise<AttemptScore<A, Z> | undefined> {
		// Pulls data from global scores map
		return new Promise(resolve => {
			try { resolve(scores); }
			catch (e) { resolve(undefined); }
		});
	}

	/**
	 * @returns {Promise<ReviewScore[]>}
	 */
	getTableScores(): Promise<ReviewScore[]> {
		return new Promise(resolve => {
			const columns = Array.from(
				document.querySelectorAll('table.scores thead tr th')
			).map((header: HTMLElement) => header.dataset.name);
			const sections = Array.from(
				document.querySelectorAll('table.scores tbody tr')
			).map(row => Array.from(
					row.querySelectorAll('td')
				).map((cell, index) => ({
					key: columns[index],
					value: /^[-\d%.\s]+$/.test(cell.textContent) ?
						parseFloat(cell.textContent):
						cell.textContent
				})).reduce((result, cell) => {
					result[cell.key] = cell.value;
					return result;
				}, {} as ReviewScore)
			);
			resolve(sections);
		});
	}

	/**
	 * @returns {Promise<ReviewScore>}
	 */
	getTotalScores(): Promise<ReviewScore> {
		return new Promise(resolve => {
			const columns = Array.from(
				document.querySelectorAll('table.scores thead tr th')
			).map((header: HTMLElement) => header.dataset.name);
			const total = Array.from(
				document.querySelectorAll('table.scores tfoot tr th')
			).map((cell, index) => ({
				key: columns[index],
				value: /^[-\d%.\s]+$/.test(cell.textContent) ?
					parseFloat(cell.textContent):
					cell.textContent
			})).reduce((result, cell) => {
				result[cell.key] = cell.value;
				return result;
			}, {} as ReviewScore);
			resolve(total);
		});
	}
}

export class NullScoreDataSource<A extends AttemptId, Z extends QuizId> extends ScoreDataSource<A, Z> {
}